/**
 * Справочник таблетницы: подписи, иконки и словесные описания правил.
 *
 * Вынесен из компонентов, потому что одни и те же формулировки нужны и в
 * карточке курса, и в ленте «сегодня», и в форме. Держать их в одном месте
 * дешевле, чем ловить расхождения «3 раза в день» / «трижды в сутки» по экранам.
 */

import {
  Bandage,
  Blend,
  Container,
  Cylinder,
  Droplet,
  Droplets,
  FlaskConical,
  Layers,
  type LucideIcon,
  Milk,
  Pill,
  SprayCan,
  Syringe,
  Tablets,
  TestTube,
  Wind,
} from 'lucide-react';

import type {
  CourseType,
  DoseUnit,
  MealRelation,
  MedScheduleType,
  Medication,
  MedicationForm,
  SkipReason,
} from '@/shared/db/schema';
import {
  type IsoDate,
  formatDateShort,
  formatDuration,
  plural,
  type MinutesOfDay,
} from '@/shared/lib/datetime';

import { courseDaysLeft, dosesPerDay } from './dose-scheduler';
import { describeWeekdays } from '@/shared/lib/weekdays';

/* ═══════════════════════ Форма выпуска ═══════════════════════ */

interface FormMeta {
  label: string;
  icon: LucideIcon;
  /** Единица дозы, предлагаемая по умолчанию при выборе формы. */
  defaultUnit: DoseUnit;
  /** Склонение для штучных форм: «1 таблетка / 2 таблетки / 5 таблеток». */
  countable: [one: string, few: string, many: string];
}

export const MEDICATION_FORM_META: Record<MedicationForm, FormMeta> = {
  tablet: {
    label: 'Таблетки',
    icon: Tablets,
    defaultUnit: 'pcs',
    countable: ['таблетка', 'таблетки', 'таблеток'],
  },
  capsule: {
    label: 'Капсулы',
    icon: Pill,
    defaultUnit: 'pcs',
    countable: ['капсула', 'капсулы', 'капсул'],
  },
  syrup: { label: 'Сироп', icon: Milk, defaultUnit: 'ml', countable: ['доза', 'дозы', 'доз'] },
  suspension: {
    label: 'Суспензия',
    icon: FlaskConical,
    defaultUnit: 'ml',
    countable: ['доза', 'дозы', 'доз'],
  },
  solution: {
    label: 'Раствор',
    icon: TestTube,
    defaultUnit: 'ml',
    countable: ['доза', 'дозы', 'доз'],
  },
  injection: {
    label: 'Инъекция',
    icon: Syringe,
    defaultUnit: 'ml',
    countable: ['укол', 'укола', 'уколов'],
  },
  drops: {
    label: 'Капли',
    icon: Droplet,
    defaultUnit: 'drop',
    countable: ['капля', 'капли', 'капель'],
  },
  spray: {
    label: 'Спрей',
    icon: SprayCan,
    defaultUnit: 'puff',
    countable: ['впрыск', 'впрыска', 'впрысков'],
  },
  ointment: { label: 'Мазь', icon: Blend, defaultUnit: 'g', countable: ['доза', 'дозы', 'доз'] },
  gel: { label: 'Гель', icon: Droplets, defaultUnit: 'g', countable: ['доза', 'дозы', 'доз'] },
  // Рядом с мазью и гелем: наружные средства держатся вместе, а выбирать между
  // ними человек будет по тому, что написано на упаковке. Раньше крем
  // приходилось заводить мазью — и в журнале стояло не то, чем лечатся.
  cream: { label: 'Крем', icon: Layers, defaultUnit: 'g', countable: ['доза', 'дозы', 'доз'] },
  patch: {
    label: 'Пластырь',
    icon: Bandage,
    defaultUnit: 'pcs',
    countable: ['пластырь', 'пластыря', 'пластырей'],
  },
  suppository: {
    label: 'Свечи',
    icon: Cylinder,
    defaultUnit: 'pcs',
    countable: ['свеча', 'свечи', 'свечей'],
  },
  powder: {
    label: 'Порошок',
    icon: Container,
    defaultUnit: 'sachet',
    countable: ['пакетик', 'пакетика', 'пакетиков'],
  },
  inhaler: {
    label: 'Ингалятор',
    icon: Wind,
    defaultUnit: 'puff',
    countable: ['вдох', 'вдоха', 'вдохов'],
  },
};

export const MEDICATION_FORM_OPTIONS = (Object.keys(MEDICATION_FORM_META) as MedicationForm[]).map(
  (value) => ({ value, label: MEDICATION_FORM_META[value].label }),
);

/* ═══════════════════════ Единицы дозы ═══════════════════════ */

interface UnitMeta {
  label: string;
  /** Короткая форма рядом с числом. */
  short: string;
  countable: [one: string, few: string, many: string] | null;
}

export const DOSE_UNIT_META: Record<DoseUnit, UnitMeta> = {
  pcs: { label: 'штуки', short: 'шт', countable: null },
  mg: { label: 'миллиграммы', short: 'мг', countable: null },
  g: { label: 'граммы', short: 'г', countable: null },
  ml: { label: 'миллилитры', short: 'мл', countable: null },
  drop: { label: 'капли', short: 'кап', countable: ['капля', 'капли', 'капель'] },
  iu: { label: 'международные единицы', short: 'МЕ', countable: null },
  puff: { label: 'впрыски', short: 'впр', countable: ['впрыск', 'впрыска', 'впрысков'] },
  sachet: { label: 'пакетики', short: 'пак', countable: ['пакетик', 'пакетика', 'пакетиков'] },
};

export const DOSE_UNIT_OPTIONS = (Object.keys(DOSE_UNIT_META) as DoseUnit[]).map((value) => ({
  value,
  label: `${DOSE_UNIT_META[value].short} — ${DOSE_UNIT_META[value].label}`,
}));

/** Число без «хвоста» из нулей: 1 → «1», 0.5 → «0,5». */
export function formatAmount(amount: number): string {
  const rounded = Math.round(amount * 100) / 100;
  return String(rounded).replace('.', ',');
}

/**
 * Доза человеческим языком: «2 таблетки», «10 капель», «500 мг».
 * Для штучных форм используется название формы — так понятнее, чем «2 шт».
 */
export function formatDose(amount: number, unit: DoseUnit, form: MedicationForm): string {
  const value = formatAmount(amount);
  const whole = Math.round(amount);
  const isWhole = Math.abs(amount - whole) < 0.001;

  if (unit === 'pcs') {
    const [one, few, many] = MEDICATION_FORM_META[form].countable;
    return isWhole ? `${value} ${plural(whole, one, few, many)}` : `${value} ${many}`;
  }

  const meta = DOSE_UNIT_META[unit];
  if (meta.countable && isWhole) {
    const [one, few, many] = meta.countable;
    return `${value} ${plural(whole, one, few, many)}`;
  }
  return `${value} ${meta.short}`;
}

/** Доза препарата из карточки. */
export function formatMedicationDose(medication: Medication): string {
  return formatDose(medication.doseAmount, medication.doseUnit, medication.form);
}

/* ═══════════════════════ Связь с едой ═══════════════════════ */

export const MEAL_RELATION_META: Record<MealRelation, { label: string; usesOffset: boolean }> = {
  any: { label: 'Не важно', usesOffset: false },
  beforeMeal: { label: 'До еды', usesOffset: true },
  withMeal: { label: 'Во время еды', usesOffset: false },
  afterMeal: { label: 'После еды', usesOffset: true },
  emptyStomach: { label: 'Натощак', usesOffset: false },
  bedtime: { label: 'Перед сном', usesOffset: false },
};

export const MEAL_RELATION_OPTIONS = (Object.keys(MEAL_RELATION_META) as MealRelation[]).map(
  (value) => ({ value, label: MEAL_RELATION_META[value].label }),
);

/** «За 30 мин до еды», «через час после еды», «натощак». */
export function describeMealRelation(relation: MealRelation, offsetMin: number): string {
  const offset = Math.max(0, Math.round(offsetMin));

  switch (relation) {
    case 'any':
      return '';
    case 'beforeMeal':
      return offset > 0 ? `за ${formatDuration(offset)} до еды` : 'до еды';
    case 'afterMeal':
      return offset > 0 ? `через ${formatDuration(offset)} после еды` : 'после еды';
    case 'withMeal':
      return 'во время еды';
    case 'emptyStomach':
      return 'натощак';
    case 'bedtime':
      return 'перед сном';
  }
}

/* ═══════════════════════ Курс и расписание ═══════════════════════ */

export const COURSE_TYPE_META: Record<CourseType, { label: string; hint: string }> = {
  daysCount: { label: 'N дней', hint: 'Курс фиксированной длины' },
  untilDate: { label: 'До даты', hint: 'Приём до конкретного дня включительно' },
  indefinite: { label: 'Бессрочно', hint: 'Постоянная терапия без даты окончания' },
  asNeeded: { label: 'По необходимости', hint: 'Приём по факту, расписание не строится' },
  none: { label: 'Не принимаю', hint: 'Просто лежит в аптечке: приёмов и напоминаний нет' },
};

export const COURSE_TYPE_OPTIONS = (Object.keys(COURSE_TYPE_META) as CourseType[]).map((value) => ({
  value,
  label: COURSE_TYPE_META[value].label,
}));

export const SCHEDULE_TYPE_META: Record<MedScheduleType, { label: string }> = {
  timesPerDay: { label: 'N раз в день' },
  everyNHours: { label: 'Каждые N часов' },
  weekdays: { label: 'Дни недели' },
  everyNDays: { label: 'Каждые N дней' },
  cycle: { label: 'Циклами' },
};

export const SCHEDULE_TYPE_OPTIONS = (Object.keys(SCHEDULE_TYPE_META) as MedScheduleType[]).map(
  (value) => ({ value, label: SCHEDULE_TYPE_META[value].label }),
);

/**
 * Дни приёма строчными буквами: описание встраивается в строку расписания.
 * Само правило — общее, из `shared/lib/weekdays`.
 */
export function describeDoseWeekdays(mask: number): string {
  return describeWeekdays(mask, { lower: true });
}

/** Короткое описание расписания для карточки курса. */
export function describeSchedule(medication: Medication): string {
  if (medication.courseType === 'none') return 'Лежит в аптечке';
  if (medication.courseType === 'asNeeded') return 'По необходимости';

  switch (medication.scheduleType) {
    case 'timesPerDay': {
      const times = Math.max(1, Math.round(medication.timesPerDay));
      return `${times} ${plural(times, 'раз', 'раза', 'раз')} в день`;
    }
    case 'everyNHours': {
      const hours = Math.max(1, Math.round(medication.intervalHours));
      return `каждые ${hours} ${plural(hours, 'час', 'часа', 'часов')}`;
    }
    case 'weekdays':
      // Строчными: описание встраивается в строку рядом с «3 раза в день».
      return describeDoseWeekdays(medication.weekdaysMask);
    case 'everyNDays': {
      const days = Math.max(1, Math.round(medication.intervalDays));
      return days === 1 ? 'ежедневно' : `каждые ${days} ${plural(days, 'день', 'дня', 'дней')}`;
    }
    case 'cycle':
      return `${medication.cycleActiveDays} через ${medication.cyclePauseDays}`;
  }
}

/** Описание курса: длительность и сколько осталось. */
export function describeCourse(medication: Medication, today: IsoDate): string {
  switch (medication.courseType) {
    case 'none':
      return 'Лежит в аптечке';
    case 'asNeeded':
      return 'Без курса';
    case 'indefinite':
      return 'Бессрочно';
    case 'daysCount': {
      const days = medication.durationDays ?? 0;
      const left = courseDaysLeft(medication, today);
      const total = `Курс ${days} ${plural(days, 'день', 'дня', 'дней')}`;
      if (left === null) return total;
      return left > 0
        ? `${total} · осталось ${left} ${plural(left, 'день', 'дня', 'дней')}`
        : `${total} · завершён`;
    }
    case 'untilDate': {
      if (medication.endDate === null) return 'До даты';
      const left = courseDaysLeft(medication, today);
      return left !== null && left > 0
        ? `До ${formatDateShort(medication.endDate)} · осталось ${left} ${plural(left, 'день', 'дня', 'дней')}`
        : `До ${formatDateShort(medication.endDate)} · завершён`;
    }
  }
}

/** «3 приёма в день» — для подписи под названием препарата. */
export function describeDailyLoad(medication: Medication): string {
  const perDay = dosesPerDay(medication);
  if (perDay === 0) return '';
  return `${perDay} ${plural(perDay, 'приём', 'приёма', 'приёмов')} в день`;
}

/* ═══════════════════════ Причины пропуска ═══════════════════════ */

/*
  Подписи без рода: «Забыл» — про мужчину, «Забыла» — про женщину, а
  приложением пользуются и те, и другие. Причина пропуска говорит о том, что
  произошло, а не о том, кто это был.
*/
export const SKIP_REASON_META: Record<SkipReason, { label: string }> = {
  forgot: { label: 'Вылетело из головы' },
  sideEffect: { label: 'Побочный эффект' },
  ranOut: { label: 'Кончился запас' },
  feltBetter: { label: 'Стало лучше' },
  other: { label: 'Другое' },
};

export const SKIP_REASON_OPTIONS = (Object.keys(SKIP_REASON_META) as SkipReason[]).map((value) => ({
  value,
  label: SKIP_REASON_META[value].label,
}));

/* ═══════════════════════ Время приёма ═══════════════════════ */

/** Часть суток — заголовок группы в ленте «сегодня». */
export function partOfDay(minutes: MinutesOfDay): string {
  if (minutes < 300) return 'Ночь';
  if (minutes < 720) return 'Утро';
  if (minutes < 1020) return 'День';
  if (minutes < 1320) return 'Вечер';
  return 'Ночь';
}
